import React from 'react';
import {
  FileText,
  Settings2,
  ShieldCheck,
  Wrench,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const STATUS_BADGE_CLASSES = {
  urgent: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/25 dark:bg-red-500/10 dark:text-red-300',
  attention: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-300',
  next: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/25 dark:bg-sky-500/10 dark:text-sky-300',
  steady: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-400/20 dark:bg-slate-400/10 dark:text-slate-300',
};

const METRIC_CARD_CLASSES = {
  urgent: 'border-red-200 bg-red-50/90 dark:border-red-500/20 dark:bg-red-500/5',
  attention: 'border-amber-200 bg-amber-50/90 dark:border-amber-500/20 dark:bg-amber-500/5',
  next: 'border-sky-200 bg-sky-50/90 dark:border-sky-500/20 dark:bg-sky-500/5',
  steady: 'border-slate-200 bg-white/90 dark:border-white/10 dark:bg-white/[0.03]',
};

function StageBadge({ tone, children }) {
  return (
    <Badge className={STATUS_BADGE_CLASSES[tone] || STATUS_BADGE_CLASSES.steady}>
      {children}
    </Badge>
  );
}

function MetricCard({ metric }) {
  return (
    <Card className={`rounded-[1.2rem] border p-4 ${METRIC_CARD_CLASSES[metric.tone] || METRIC_CARD_CLASSES.steady}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-gray-500">
        {metric.label}
      </p>
      <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">
        {metric.value}
      </p>
      <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
        {metric.detail}
      </p>
    </Card>
  );
}

function SystemItemCard({ item, onOpenTool }) {
  return (
    <div className={`rounded-[1.15rem] border p-4 ${METRIC_CARD_CLASSES[item.tone] || METRIC_CARD_CLASSES.steady}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">
            {item.eyebrow}
          </p>
          <p className="mt-3 text-base font-semibold text-slate-900 dark:text-white">
            {item.title}
          </p>
        </div>
        <StageBadge tone={item.tone}>
          {item.location}
        </StageBadge>
      </div>
      <p className="mt-3 text-sm leading-6 text-slate-700 dark:text-gray-300">
        {item.detail}
      </p>
      {item.action ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenTool?.(item.action.tab, item.action.roomId, item.action.toolContext)}
          className="mt-4 border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
        >
          {item.action.label}
        </Button>
      ) : null}
    </div>
  );
}

function SectionCard({ eyebrow, title, detail, tone = 'steady', items = [], emptyTitle, emptyDetail, onOpenTool }) {
  return (
    <Card className={`border p-5 ${METRIC_CARD_CLASSES[tone] || METRIC_CARD_CLASSES.steady}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="max-w-2xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-slate-500 dark:text-gray-500">
            {eyebrow}
          </p>
          <h4 className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">
            {title}
          </h4>
          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
            {detail}
          </p>
        </div>
        <div className="rounded-full bg-white/80 p-3 text-slate-700 shadow-sm dark:bg-black/20 dark:text-gray-200">
          {tone === 'attention' ? <Wrench className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {items.length ? (
          items.map((item) => (
            <SystemItemCard key={item.id} item={item} onOpenTool={onOpenTool} />
          ))
        ) : (
          <div className="rounded-[1.15rem] border border-dashed border-slate-200 bg-white/70 px-4 py-8 text-center dark:border-white/10 dark:bg-black/10">
            <p className="text-base font-semibold text-slate-900 dark:text-white">
              {emptyTitle}
            </p>
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
              {emptyDetail}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

function SuggestedActionCard({ action, onOpenTool }) {
  return (
    <div className={`rounded-[1.15rem] border p-4 ${METRIC_CARD_CLASSES[action.tone] || METRIC_CARD_CLASSES.steady}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-gray-500">
        {action.eyebrow}
      </p>
      <h4 className="mt-3 text-base font-semibold text-slate-900 dark:text-white">
        {action.title}
      </h4>
      <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-gray-300">
        {action.detail}
      </p>
      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">
          {action.location}
        </p>
        {action.action ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenTool?.(action.action.tab, action.action.roomId, action.action.toolContext)}
            className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
          >
            {action.action.label}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default function SystemHealthRoom({ context }) {
  return (
    <div className="grid gap-4">
      <Card className="border-slate-200 bg-gradient-to-br from-white via-[#f8fafc] to-[#eef3f7] p-5 dark:border-white/10 dark:bg-gradient-to-br dark:from-[#20232a] dark:via-[#171b20] dark:to-[#10161b]">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-700 dark:text-sky-300">
                Admin upkeep room
              </p>
              <h3 className="mt-3 text-2xl font-semibold leading-8 text-slate-900 dark:text-white">
                {context.startTitle}
              </h3>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
                {context.startDetail}
              </p>
            </div>
            <StageBadge tone={context.statusTone}>
              {context.statusLabel}
            </StageBadge>
          </div>

          <div className="grid gap-2 md:grid-cols-3">
            {(context.signals || []).map((signal, index) => (
              <div key={`${signal.text}-${index}`} className="rounded-2xl border border-slate-200 bg-white/80 px-3 py-3 dark:border-white/10 dark:bg-white/[0.04]">
                <p className="text-sm leading-6 text-slate-700 dark:text-gray-300">
                  {signal.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <div className="grid gap-3 md:grid-cols-3">
        {(context.metrics || []).map((metric, index) => (
          <MetricCard key={`${metric.label}-${index}`} metric={metric} />
        ))}
      </div>
      <Card className="border-sky-200 bg-gradient-to-br from-[#eef5fb] via-[#f8fbfd] to-[#edf4f8] p-5 dark:border-sky-500/15 dark:bg-gradient-to-br dark:from-[#141d24] dark:via-[#11171d] dark:to-[#0f1419]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-sky-700 dark:text-sky-300">
              Recommended next moves
            </p>
            <h4 className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">
              Improve the system when things are calm
            </h4>
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
              This room is for the slower upkeep work that should not compete with live monitoring: document gaps, settings drift, and quiet maintenance that makes the admin surface easier to trust over time.
            </p>
          </div>
          <div className="flex items-center gap-2 text-sky-700 dark:text-sky-300">
            <FileText className="h-5 w-5" />
            <Settings2 className="h-5 w-5" />
          </div>
        </div>

        <div className="mt-5 grid gap-3 xl:grid-cols-3">
          {(context.suggestedActions || []).map((action, index) => (
            <SuggestedActionCard key={`${action.title}-${index}`} action={action} onOpenTool={context.onOpenTool} />
          ))}
        </div>

        {(context.primaryAction?.label || context.secondaryAction?.label) ? (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <p className="mr-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-gray-500">
              Suggested flow
            </p>
            {context.primaryAction?.label ? (
              <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white/80 px-3 py-1.5 text-sm text-sky-900 dark:border-sky-500/20 dark:bg-black/20 dark:text-sky-300">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700 dark:text-sky-300">
                  Start
                </span>
                <span className="font-medium">{context.primaryAction.label}</span>
              </div>
            ) : null}
            {context.secondaryAction?.label ? (
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 text-sm text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-gray-300">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">
                  Then
                </span>
                <span className="font-medium">{context.secondaryAction.label}</span>
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>
    </div>
  );
}
